import React, { FC, ReactElement } from 'react'
import { Trans, t } from '@lingui/macro'
import { TableInlineTooltip } from '../../../../components/common/tooltip/TableInlineTooltip'
import IconFont from '../../../../components/common/IconFont'
import { CampaignActionTime } from '../../campaign-actions/CampaignActionTime'
import { ScheduledCampaignActionModel } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useNextScheduledCampaignActionQuery } from './NextActionColumn.codegen'
import dayjs from 'shared/dayjs'

export const NextActionColumn: FC<{
  scheduled_time: string
  id: number
  campaignId: number
}> = (props) => {
  const { data } = useNextScheduledCampaignActionQuery({
    variables: {
      campaignId: props.campaignId,
      actionId: props.id
    }
  })

  let content: ReactElement | null = null
  if (data) {
    const titleMap = {
      campaign_email_action_id: t`Send email`,
      campaign_banner_id: data.admin?.campaign?.scheduledAction?.isBannerStarted
        ? t`Hide banner`
        : t`Display banner`,
      campaign_topic_share_action_id: t`Share resource`,
      campaign_pathway_share_action_id: t`Share pathway`,
      campaign_message_action_id: t`Send message`,
      campaign_survey_share_action_id: t`Share survey`,
      campaign_certification_share_action_id: t`Share certification`
    }

    const scheduledAction = data?.admin?.campaign?.scheduledAction
    if (scheduledAction) {
      let title = ''

      for (const titleMapKey in titleMap) {
        if (scheduledAction[titleMapKey] !== null) {
          title = titleMap[titleMapKey]
          break
        }
      }

      content = (
        <div>
          <i>
            <Trans>Next action</Trans>:
          </i>
          <h4>{title}</h4>
          <p>
            {renderNextActionDescription(
              scheduledAction as Partial<ScheduledCampaignActionModel>
            )}
          </p>
          <small className={'Card__subtitle'}>
            <CampaignActionTime
              timeWithTimezone={
                scheduledAction.emailAction?.send_at_time ||
                scheduledAction.topicShareAction?.send_at_time ||
                scheduledAction.pathwayShareAction?.send_at_time ||
                scheduledAction.messageAction?.send_at_time ||
                scheduledAction.surveyShareAction?.send_at_time
              }
              businessHoursEnabled={
                scheduledAction.emailAction?.respect_business_hours ||
                scheduledAction.topicShareAction?.respect_business_hours ||
                scheduledAction.pathwayShareAction?.respect_business_hours ||
                scheduledAction.messageAction?.respect_business_hours ||
                scheduledAction.surveyShareAction?.respect_business_hours
              }
            />
          </small>
        </div>
      )
    }
  }

  return (
    <div>
      <TableInlineTooltip content={content}>
        {dayjs(props.scheduled_time).format('lll')} <IconFont type={'info'} />
      </TableInlineTooltip>
    </div>
  )
}

const renderNextActionDescription = (
  scheduledAction: Partial<ScheduledCampaignActionModel>
) => {
  if (scheduledAction.banner) {
    return scheduledAction.banner.header
  }

  if (scheduledAction.emailAction) {
    return scheduledAction.emailAction.subject
  }

  if (scheduledAction.topicShareAction) {
    const topicsCount = scheduledAction.topicShareAction.actionTopics.length
    return topicsCount > 1
      ? t`${topicsCount} resources`
      : scheduledAction.topicShareAction.actionTopics[0].topic.currentTitle
  }

  if (scheduledAction.pathwayShareAction) {
    const pathwaysCount =
      scheduledAction.pathwayShareAction.actionPathways.length
    return pathwaysCount > 1
      ? t`${pathwaysCount} pathways`
      : scheduledAction.pathwayShareAction.actionPathways[0].pathway.title
  }

  if (scheduledAction.surveyShareAction) {
    const surveysCount = scheduledAction.surveyShareAction.actionSurveys.length
    return surveysCount > 1
      ? t`${surveysCount} surveys`
      : scheduledAction.surveyShareAction.actionSurveys[0].survey.title
  }

  if (scheduledAction.messageAction) {
    return `${scheduledAction.messageAction.text?.substring(0, 20)} ${
      (scheduledAction.messageAction.text?.length ?? 0) > 20 ? '...' : ''
    }`
  }
}
